
import { FlagCollectionItemT } from 'bovaljaren-models';
import React, { ReactElement } from 'react';
import BrandingService from '../../../../services/branding.service';
import { isMobile } from '../../../../utils/app.helper';
import './Flag.scss';

type Props={
    className?: string;
    flag: FlagCollectionItemT;
    onClick?: () => void;
    onMouseOver?: React.MouseEventHandler<HTMLDivElement> | undefined;
    onMouseOut?: React.MouseEventHandler<HTMLDivElement> | undefined;
};

const Flag: React.FC<Props> = ({ className = '', flag, onClick, onMouseOut, onMouseOver }): ReactElement => {
    const inverted = flag.specs.width < 0;
    const standing = flag.specs.height > 0;
    const polygon = false;
    const flagdot = true;


    const flagPoints = () => {
        const offset = 100;

        const settings = {
            invertedTop: `100,100 100,${100 - offset} 0,${100 - offset} 0,${100 - offset + 4} 95,${100 - offset + 4}  99,100  100,100`,
            invertedBottom: `100,0 100,${offset} 0,${offset} 0,${ offset - 4} 95, ${ offset - 4}  99,0  100,0`,
            regularTop:     `0,100 0,${100 - offset} 100,${100 - offset} 100,${100 - offset + 4} 5,${100 - offset + 4} 1,100 0,100`,
            regularBottom: `0,0 0,${offset} 100,${offset} 100,${offset - 4} 5, ${offset - 4} 1,0 0,0`,
            standingDouble: `100,100 100,${100 - offset} 0,${100 - offset} 0,100 1,100 4,5 96,5 99,100`,
            invertedDouble: `100,0 100,${offset} 0,${offset} 0,0 1,0 4,95 96,95 99,0`
        };

        if (flag.specs.double && standing) {
            return settings.standingDouble;
        }
        else if (flag.specs.double && !standing) {
            return settings.invertedDouble;
        }
        else if (standing && !inverted) {
            return settings.regularTop;
        }
        else if (standing && inverted) {
            return settings.invertedTop;
        }
        else if (!standing && inverted) {
            return settings.invertedBottom;
        }
        else if (!standing && !inverted) {
            return settings.regularBottom;
        }
    };

    const getPolygon = () => {
        return <polygon
            className={'bv-flag-polygon'}
            points={flagPoints()}
        />;
    };

    const hideBorders = (): { borderBottom?: string; borderTop?: string; borderLeft?: string; borderRight?: string } => {
        const base = { borderBottom: 'none', borderTop: 'none' };

        if (flag.specs.double) {
            return base;
        }
        else {
            return { ...base, borderLeft: 'none' };
        }
    };

    const getCircleStyle = (inverted: boolean, topDown: boolean, double = false): object => {
        let base = 0.5;

        if (isMobile())
            base = 0.25;

        if (inverted && topDown) {
            return { right: !double ? `-${base}rem` : `-${base * (isMobile() ? 2 : 1.1)}rem`, top: `-${base}rem` };
        }
        else if (inverted && !topDown) {
            return { right: !double ? `-${base}rem` : `-${base * (isMobile() ? 2 : 1.1)}rem`, bottom: `-${base}rem` };
        }
        else if (!inverted && topDown) {
            return { left: !double ? `-${base}rem` : `-${base * (isMobile() ? 2 : 1.1)}rem`, top: `-${base}rem` };
        }
        else {
            return { bottom: !double ? `-${base}rem` : `-${base * (isMobile() ? 2 : 1.1)}rem`, left: `-${base}rem` };
        }
    };

    const getInnerCircleStyle = (inverted: boolean, topDown: boolean, double = false): object => {
        let base = 0.19;

        if (isMobile())
            base = 0.05;

        return { top: `${base}rem` };
    };
    const brandingWidth = () => {
        return { width: 'calc(100% + 1rem', marginLeft: '-0.5rem' };
    };

    return (<div className={`bv-flag ${className}`}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={onClick}
        style={{
            fill: 'white',
            position:'absolute',
            textAlign: 'center',
            top:`calc(${!standing ? flag.specs.relativePosition.top : flag.specs.relativePosition.top - flag.specs.height}% ${!standing ? '+' : '-'} 0.2%)`,
            left:`${inverted ? flag.specs.relativePosition.left + flag.specs.width : flag.specs.relativePosition.left}%`,
            width:`${isMobile() ? Math.abs(flag.specs.width) + 2 : Math.abs(flag.specs.width)}%`,
            height:`${Math.abs(flag.specs.height)}%`
        }}>
        {standing && <div
            onClick={onClick} className={'bv-flag-text-wrapper'}>
            <p id="text" className={'bv-flag-text bv-flag-text--top'} style={{ ...brandingWidth() }}>{flag.text}</p>
        </div>}
        {polygon && <svg
            version="1.1"
            viewBox='0 0 100 100'
            onClick={onClick}
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg">
            {getPolygon()}
        </svg>}
        {!polygon && <div className={'bv-flag-pole'} style={{ width: flag.specs.double ? (isMobile() ? 'calc(100% - 0.32rem)' : 'calc(100% - 0.44rem)') : (isMobile() ? 'calc(50% - 0.16rem)' : 'calc(50% - 0.22rem)'), height: '100%', border: isMobile() ? '0.16rem solid white' : '0.22rem solid white', ...hideBorders() }}>
        </div>}
        {!standing && <div
            onClick={onClick} className={'bv-flag-text-wrapper'} style={{ width: 'calc(100% + 1rem' }}>
            <p id="text" style={{ top: 'initial', position: 'absolute', display: 'inline-block', ...brandingWidth() }} className={'bv-flag-text  bv-flag-text--bottom'}>{flag.text}</p>
        </div>}
    </div>);
};


export default Flag;